import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useQueryCRUD } from '../useQueryCRUD';
import servicesService from '../../services/servicesService';
import professionalsService from '../../services/professionalsService';
import personsService from '../../services/personsService';
import placesService from '../../services/placesService';
import schedulesService from '../../services/schedulesService';
import paymentsService from '../../services/paymentsService';
import pagesService from '../../services/pagesService';
import scheduleBlocksService from '../../services/scheduleBlocksService';
import entitiesService from '../../services/entitiesService';
import api from '../../services/api';

/**
 * Hook para gerenciar Serviços com React Query
 */
export function useServices(options) {
  return useQueryCRUD('services', servicesService, options);
}

/**
 * Hook para gerenciar Profissionais com React Query
 */
export function useProfessionals(options) {
  return useQueryCRUD('professionals', professionalsService, options);
}

/**
 * Hook para gerenciar Pessoas com React Query
 */
export function usePersons(options) {
  return useQueryCRUD('persons', personsService, options);
}

/**
 * Hook para gerenciar Locais com React Query
 */
export function usePlaces(options) {
  return useQueryCRUD('places', placesService, options);
}

/**
 * Hook para gerenciar Agendas com React Query
 * Inclui dados relacionados (professionals, places, placeProfessionals)
 */
export function useSchedules(options = {}) {
  const queryClient = useQueryClient();

  // Query para listar schedules com dados relacionados
  const query = useQuery({
    queryKey: ['schedules'],
    queryFn: async () => {
      const data = await schedulesService.getAllWithRelations();
      return data;
    },
    staleTime: options.staleTime ?? 5 * 60 * 1000, // 5 minutos
    refetchOnMount: 'always', // sempre refetch ao montar (evita dado stale pós-wizard)
    ...options.queryOptions,
  });

  // Mutation para criar
  const createMutation = useMutation({
    mutationFn: (data) => schedulesService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
    },
  });

  // Mutation para atualizar
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => schedulesService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
    },
  });

  // Mutation para deletar
  const deleteMutation = useMutation({
    mutationFn: (id) => schedulesService.delete(id),
    onSuccess: (_, id) => {
      // Remove imediatamente do cache para evitar que loadShiftsForAgendas
      // tente buscar turnos de uma agenda já deletada (race condition com staleTime)
      queryClient.setQueryData(['schedules'], (old) => {
        if (!old) return old;
        return {
          ...old,
          schedules: old.schedules?.filter((s) => s.id !== id) || [],
        };
      });
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
    },
  });

  return {
    // Data normalizada
    schedules: query.data?.schedules || [],
    professionals: query.data?.professionals || [],
    places: query.data?.places || [],
    
    // Loading states
    loading: query.isLoading,
    isFetching: query.isFetching,
    
    // Error
    error: query.error,
    
    // Actions
    reload: () => queryClient.invalidateQueries({ queryKey: ['schedules'] }),
    
    // CRUD operations
    create: createMutation.mutate,
    createAsync: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
    
    update: (id, data) => updateMutation.mutate({ id, data }),
    updateAsync: (id, data) => updateMutation.mutateAsync({ id, data }),
    isUpdating: updateMutation.isPending,
    
    deleteItem: deleteMutation.mutate,
    deleteAsync: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,
  };
}

/**
 * Hook para gerenciar Agendas (alias para compatibilidade)
 */
export function useAgendas(options) {
  return useSchedules(options);
}

/**
 * Hook para gerenciar Bloqueios/Ausências com React Query e paginação
 */
export function useScheduleBlocks(options = {}) {
  const queryClient = useQueryClient();
  const { page = 1, pageSize = 10, filters = {} } = options;

  // Query para listar bloqueios com paginação
  const query = useQuery({
    queryKey: ['scheduleBlocks', page, pageSize, filters],
    queryFn: async () => {
      const data = await scheduleBlocksService.getAll(page, pageSize, filters);
      return data;
    },
    staleTime: options.staleTime ?? 5 * 60 * 1000,
    placeholderData: keepPreviousData,
    ...options.queryOptions,
  });

  // Mutation para criar
  const createMutation = useMutation({
    mutationFn: (data) => scheduleBlocksService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduleBlocks'] });
    },
  });

  // Mutation para atualizar
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => scheduleBlocksService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduleBlocks'] });
    },
  });

  // Mutation para deletar
  const deleteMutation = useMutation({
    mutationFn: (id) => scheduleBlocksService.delete(id),
    onSuccess: (_, id) => {
      queryClient.setQueryData(['scheduleBlocks', page, pageSize, filters], (old) => {
        if (!old) return old;
        return {
          ...old,
          scheduleBlocks: old.scheduleBlocks?.filter((b) => b.id !== id) || [],
        };
      });
      queryClient.invalidateQueries({ queryKey: ['scheduleBlocks'] });
    },
  });

  return {
    // Data
    data: query.data,
    scheduleBlocks: query.data?.scheduleBlocks || [],
    blocks: query.data?.scheduleBlocks || [], // alias para compatibilidade
    professionals: query.data?.professionals || [],
    places: query.data?.places || [],
    totalCount: query.data?.pagination?.totalItems || 0,
    totalPages: query.data?.pagination?.totalPages || 0,
    currentPage: query.data?.pagination?.currentPage || 1,
    pageSize: query.data?.pagination?.pageSize || 10,
    
    // Loading states
    loading: query.isLoading,
    isFetching: query.isFetching,
    
    // Error
    error: query.error,
    
    // Actions
    reload: () => queryClient.invalidateQueries({ queryKey: ['scheduleBlocks'] }),
    
    // CRUD operations
    create: createMutation.mutate,
    createAsync: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
    
    update: (id, data) => updateMutation.mutate({ id, data }),
    updateAsync: (id, data) => updateMutation.mutateAsync({ id, data }),
    isUpdating: updateMutation.isPending,
    
    deleteItem: deleteMutation.mutate,
    deleteAsync: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,
  };
}

/**
 * Hook para gerenciar Agendamentos com React Query SEM CACHE
 * Sempre busca dados frescos do servidor
 */
export function useAppointments(options = {}) {
  const queryClient = useQueryClient();
  const { startDate, endDate } = options;

  // Query para listar agendamentos - SEM CACHE
  const query = useQuery({
    queryKey: ['appointments', startDate, endDate],
    queryFn: async () => {
      const response = await api.get('/appointments', {
        params: { startDate, endDate }
      });
      return response.data || {};
    },
    staleTime: 0, // ⚠️ SEM CACHE - sempre refetch
    cacheTime: 0, // Não manter em cache
    refetchOnWindowFocus: true,
    refetchOnMount: 'always',
    ...options.queryOptions,
  });

  // Mutation para confirmar
  const confirmMutation = useMutation({
    mutationFn: (id) => api.patch(`/appointments/${id}/confirm`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
    },
  });

  // Mutation para cancelar
  const cancelMutation = useMutation({
    mutationFn: ({ id, reason }) => api.patch(`/appointments/${id}/cancel`, { cancellationReason: reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
    },
  });

  // Mutation para marcar como atendido
  const markAttendedMutation = useMutation({
    mutationFn: (id) => api.patch(`/appointments/${id}/attended`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
    },
  });

  return {
    // Data normalizada
    appointments: query.data?.appointments || [],
    professionals: query.data?.professionals || [],
    services: query.data?.services || [],
    places: query.data?.places || [],
    
    // Loading states
    loading: query.isLoading,
    isFetching: query.isFetching,
    
    // Error
    error: query.error,
    
    // Actions
    reload: () => queryClient.invalidateQueries({ queryKey: ['appointments'] }),
    
    // Operations
    confirm: confirmMutation.mutate,
    confirmAsync: confirmMutation.mutateAsync,
    isConfirming: confirmMutation.isPending,
    
    cancel: cancelMutation.mutate,
    cancelAsync: cancelMutation.mutateAsync,
    isCanceling: cancelMutation.isPending,
    
    markAttended: markAttendedMutation.mutate,
    markAttendedAsync: markAttendedMutation.mutateAsync,
    isMarkingAttended: markAttendedMutation.isPending,
  };
}

/**
 * Hook para gerenciar Métodos de Pagamento com React Query
 */
export function usePayments(options) {
  return useQueryCRUD('payments', paymentsService, options);
}

/**
 * Hook para gerenciar Páginas com React Query
 * Expose mutations para updateDraft, publish, unpublish com invalidação automática
 */
export function usePages(options = {}) {
  const queryClient = useQueryClient();

  // Query para listar páginas
  const query = useQuery({
    queryKey: ['pages'],
    queryFn: async () => {
      const data = await pagesService.getAll();
      return data;
    },
    staleTime: options.staleTime ?? 5 * 60 * 1000, // 5 minutos
    refetchOnMount: 'always', // sempre refetch ao montar (evita dado stale pós-ação)
    ...options.queryOptions,
  });

  // Mutation para criar página (legado)
  const createMutation = useMutation({
    mutationFn: (data) => pagesService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pages'] });
    },
  });

  // Mutation para criar página site (link_in_bio v2, professional, company)
  const createSiteMutation = useMutation({
    mutationFn: (data) => pagesService.createSite(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pages'] });
    },
  });

  // Mutation para atualizar rascunho (novo ciclo draft/published)
  const updateDraftMutation = useMutation({
    mutationFn: ({ id, draftConfig }) => pagesService.updateDraft(id, draftConfig),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pages'] });
    },
  });

  // Mutation para publicar página
  const publishMutation = useMutation({
    mutationFn: (id) => pagesService.publish(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pages'] });
    },
  });

  // Mutation para despublicar página
  const unpublishMutation = useMutation({
    mutationFn: (id) => pagesService.unpublish(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pages'] });
    },
  });

  // Mutation para deletar página
  const deleteMutation = useMutation({
    mutationFn: (id) => pagesService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pages'] });
    },
  });

  return {
    // Data
    pages: query.data || [],

    // Loading states
    loading: query.isLoading,
    isFetching: query.isFetching,

    // Error
    error: query.error,

    // Actions
    reload: () => queryClient.invalidateQueries({ queryKey: ['pages'] }),

    // CRUD operations
    create: createMutation.mutate,
    createAsync: createMutation.mutateAsync,
    isCreating: createMutation.isPending,

    createSite: createSiteMutation.mutate,
    createSiteAsync: createSiteMutation.mutateAsync,
    isCreatingSite: createSiteMutation.isPending,

    updateDraft: (id, draftConfig) => updateDraftMutation.mutate({ id, draftConfig }),
    updateDraftAsync: (id, draftConfig) => updateDraftMutation.mutateAsync({ id, draftConfig }),
    isUpdatingDraft: updateDraftMutation.isPending,

    publishPage: publishMutation.mutate,
    publishPageAsync: publishMutation.mutateAsync,
    isPublishing: publishMutation.isPending,

    unpublishPage: unpublishMutation.mutate,
    unpublishPageAsync: unpublishMutation.mutateAsync,
    isUnpublishing: unpublishMutation.isPending,

    delete: deleteMutation.mutate,
    deleteAsync: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,
  };
}

/**
 * Hook para detecção e unificação de pessoas duplicadas.
 * Busca pares de duplicatas e expõe mutation de merge.
 */
export function useDuplicatePersons() {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['personDuplicates'],
    queryFn: () => personsService.getDuplicates(),
    staleTime: 5 * 60 * 1000,
  });

  const mergeMutation = useMutation({
    mutationFn: (data) => personsService.mergePersons(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['personDuplicates'] });
      queryClient.invalidateQueries({ queryKey: ['persons'] });
    },
  });

  return {
    duplicates: query.data || [],
    loading: query.isLoading,
    error: query.error,
    merge: mergeMutation.mutateAsync,
    isMerging: mergeMutation.isPending,
    reload: () => queryClient.invalidateQueries({ queryKey: ['personDuplicates'] }),
  };
}

/**
 * Hook para carregar todas as entidades do usuário com estrutura normalizada.
 * Mapeia placePayments do response para place.payments para conveniência.
 */
export function useEntities(options = {}) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['entities'],
    queryFn: async () => {
      const data = await entitiesService.getAll();

      // Mapear placePayments para place.payments
      if (data && data.places && data.placePayments) {
        const paymentsById = {};
        if (data.payments) {
          data.payments.forEach(p => {
            paymentsById[p.id] = p;
          });
        }

        data.places = data.places.map(place => ({
          ...place,
          payments: (data.placePayments || [])
            .filter(pp => pp.placeId === place.id)
            .map(pp => paymentsById[pp.paymentId])
            .filter(Boolean) // Remove undefined entries
            .sort((a, b) => (a.displayOrder ?? 999) - (b.displayOrder ?? 999))
        }));
      } else if (data && data.places) {
        // Retrocompatibilidade: se placePayments não vier, usar array vazio
        data.places = data.places.map(place => ({
          ...place,
          payments: []
        }));
      }

      return data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutos
    refetchOnWindowFocus: false,
    ...options.queryOptions,
  });

  return {
    // Data normalizada
    entities: query.data,
    professionals: query.data?.professionals || [],
    services: query.data?.services || [],
    places: query.data?.places || [],
    payments: query.data?.payments || [],
    placePayments: query.data?.placePayments || [],

    // Loading states
    loading: query.isLoading,
    isFetching: query.isFetching,

    // Error
    error: query.error,

    // Actions
    reload: () => queryClient.invalidateQueries({ queryKey: ['entities'] }),
  };
}

// Exportar todos juntos
export default {
  useServices,
  useProfessionals,
  usePersons,
  usePlaces,
  useSchedules,
  useAgendas,
  useScheduleBlocks,
  useAppointments,
  usePayments,
  usePages,
  useDuplicatePersons,
  useEntities,
};

